import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useListInventoryItems } from "@workspace/api-client-react";
import { itmId } from "@/lib/display-id";
import { computeAlertTier, ALERT_DOT_COLORS } from "@/lib/alert-tier";
import { specsToLine } from "@/lib/specs";
import { cn } from "@/lib/utils";
import { RecipePill } from "@/components/inventory/recipe-pill";

interface ItemComboboxProps {
  value: number | null;
  // 2026-05-22: meta carries the item's GST rate so stock-in can auto-fill GST %.
  onChange: (id: number, meta?: { gstRateOnSale: number | null }) => void;
  className?: string;
  autoFocus?: boolean;
  // 2026-05-21: stock-health tier is meaningful when issuing stock (stock-out) but
  // just noise when receiving (stock-in) — hide it there.
  showTier?: boolean;
  // Trigger text shown when nothing is selected. Filters pass e.g. "All items" so
  // the control reads like a filter default rather than a stray search box.
  placeholder?: string;
  // RECIPE-DISPLAY-02/03 (Phase 55, Plan 55-01): when true, the server excludes
  // recipe items from the list. Stock-In entry points (new-order line picker,
  // add-line-to-existing-order, cart-line append) pass true — recipe items are
  // cooked to order, not stocked. Read-only item filters over historical register
  // data and stock-out fast-entry MUST leave this undefined so they keep showing
  // recipe items.
  excludeRecipe?: boolean;
}

export function ItemCombobox({
  value,
  onChange,
  className,
  autoFocus,
  showTier = true,
  placeholder = "Search by item name or ITM-ID…",
  excludeRecipe,
}: ItemComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: items = [] } = useListInventoryItems({
    status: "Active",
    // Only send the flag when the caller opted in — leaving it undefined keeps
    // the request URL identical to the pre-Phase-55 shape for all read-only /
    // stock-out call sites (react-query cache key parity).
    ...(excludeRecipe ? { excludeRecipe: true } : {}),
  });

  const selected = items.find((i) => i.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          autoFocus={autoFocus}
          className={cn("w-full justify-between h-12 text-base", className)}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-mono text-xs text-muted-foreground shrink-0">{itmId(selected.id)}</span>
              <span className="shrink-0">{selected.name}</span>
              {specsToLine(selected.specs) && (
                <span className="text-xs text-muted-foreground truncate">· {specsToLine(selected.specs)}</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by item name or ITM-ID…" aria-label="Search items" />
          <CommandList>
            <CommandEmpty>No item matches your query.</CommandEmpty>
            <CommandGroup>
              {items.map((i) => {
                const tier = computeAlertTier({ currentStock: i.currentStock, reorderLevel: i.reorderLevel, safetyStock: i.safetyStock, maxStockLevel: i.maxStockLevel });
                const specsLine = specsToLine(i.specs);
                return (
                  <CommandItem
                    key={i.id}
                    // Include specs + brand in the searchable value so two same-named
                    // items (e.g. "Cold Drink") can be told apart and found by spec.
                    value={`${i.name} ${itmId(i.id)} ${i.itemCode ?? ""} ${i.brand ?? ""} ${specsLine}`}
                    onSelect={() => {
                      onChange(i.id, { gstRateOnSale: i.gstRateOnSale != null ? Number(i.gstRateOnSale) : null });
                      setOpen(false);
                    }}
                    className="py-2"
                  >
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm font-medium">{i.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{itmId(i.id)}</span>
                      </div>
                      {specsLine && (
                        <div className="text-xs text-muted-foreground">{specsLine}</div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {/* Phase 55 Plan 55-04 (RECIPE-DISPLAY-01): recipe
                            items never render an "N in stock" text — the
                            shared Recipe pill takes its place. Stock-In
                            entry points already server-side-exclude recipe
                            items via Plan 55-01's excludeRecipe filter;
                            this branch is defence-in-depth for stock-out
                            call sites (which pass excludeRecipe=undefined)
                            plus legacy carts (55-RESEARCH.md Pitfall 7). */}
                        {i.hasRecipe ? (
                          <RecipePill className="ml-1" />
                        ) : (
                          <>
                            {showTier && (
                              <span className={cn("h-2 w-2 rounded-full", ALERT_DOT_COLORS[tier])} aria-label={`Stock alert: ${tier}`} />
                            )}
                            <span>
                              {Number(i.currentStock ?? 0)} {i.unitOfMeasurement} in stock{showTier ? ` · ${tier}-tier` : ""}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    {value === i.id ? <Check className="ml-2 h-4 w-4" /> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
